import type TasApplicationModel from '../../models/tas-application.ts';
import type { LimitsStatusDataSignature } from '../../services/tuition-assistance.ts';

export type WorkflowStage =
  | 'pre-course-approval'
  | 'attend-pending-evidence'
  | 'post-evidence-approval';

export type FinancialSection = 'program-limits' | 'reimbursement-details' | 'course-cash-details';

export type FinancialField = {
  key: string;
  labelKey: string;
  section: FinancialSection;
  stages: WorkflowStage[];
  isCourseCash?: boolean;
  isPrepaidOnly?: boolean;
  isAsNeeded?: boolean;
  showIfPositiveInStage2?: boolean;
  isPercentage?: boolean;
  isBalanceMultiplier?: boolean;
  isCapacityField?: boolean;
  getValue: (app: TasApplicationModel, limitsData?: LimitsStatusDataSignature) => number | null;
};

export type FinancialFieldWithValue = Omit<FinancialField, 'getValue'> & {
  value: number | null;
  isCredits?: boolean;
};

export type FinancialSectionGroup = {
  section: FinancialSection;
  labelKey: string;
  fields: FinancialFieldWithValue[];
};

export const SECTION_LABELS: Record<FinancialSection, string> = {
  'program-limits': 'common.tas.financial_details_summary.sections.program_limits',
  'reimbursement-details': 'common.tas.financial_details_summary.sections.reimbursement_details',
  'course-cash-details': 'common.tas.financial_details_summary.sections.course_cash_details',
};

export const SECTION_ORDER: FinancialSection[] = [
  'program-limits',
  'reimbursement-details',
  'course-cash-details',
];

const toNumber = (value: unknown) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const FINANCIAL_FIELDS: FinancialField[] = [
  {
    key: 'program-limit',
    labelKey: 'common.tas.financial_details_summary.fields.program_limit',
    section: 'program-limits',
    stages: ['pre-course-approval', 'attend-pending-evidence'],
    isCapacityField: true,
    getValue: (_app, limitsData) => {
      const details = limitsData?.details;
      if (!details) return null;
      return (
        toNumber(details.scheme?.annual_limit) ??
        toNumber(details.batch?.annual_limit) ??
        toNumber(details.product?.annual_limit) ??
        toNumber(details.company?.annual_limit) ??
        null
      );
    },
  },
  {
    key: 'remaining-amount',
    labelKey: 'common.tas.financial_details_summary.fields.remaining_amount',
    section: 'program-limits',
    stages: ['pre-course-approval', 'attend-pending-evidence'],
    isCapacityField: true,
    getValue: (_app, limitsData) => toNumber(limitsData?.available),
  },
  {
    key: 'balance-multiplier',
    labelKey: 'common.tas.financial_details_summary.fields.balance_multiplier',
    section: 'program-limits',
    stages: ['pre-course-approval', 'attend-pending-evidence'],
    isPercentage: true,
    isBalanceMultiplier: true,
    getValue: (app) => {
      const multiplier =
        app.tasProgramInstance?.employee?.customAttributes?.merged?.tmBalanceMultiplier;
      if (!multiplier) return null;
      // Convert to percentage (e.g., 0.8 -> 80)
      return parseFloat(multiplier) * 100;
    },
  },
  {
    key: 'requested-amount',
    labelKey: 'common.tas.financial_details_summary.fields.requested_amount',
    section: 'reimbursement-details',
    stages: ['pre-course-approval', 'attend-pending-evidence'],
    getValue: (app) => {
      if (app.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH') {
        return toNumber(app.requestedNonCourseCashSubtotal) ?? 0;
      }

      return toNumber(app.requestedTotal) ?? 0;
    },
  },
  {
    key: 'approved-amount',
    labelKey: 'common.tas.financial_details_summary.fields.approved_amount',
    section: 'reimbursement-details',
    stages: ['attend-pending-evidence'],
    getValue: (app) => toNumber(app.approvedTotal),
  },
  {
    key: 'course-cash-requested',
    labelKey: 'common.tas.financial_details_summary.fields.course_cash_requested',
    section: 'course-cash-details',
    stages: ['pre-course-approval'],
    isCourseCash: true,
    getValue: (app) => toNumber(app.cardPreApprovedTotal) ?? 0,
  },
  {
    key: 'pending-payment-amount',
    labelKey: 'common.tas.financial_details_summary.fields.pending_payment_amount',
    section: 'reimbursement-details',
    stages: ['attend-pending-evidence', 'post-evidence-approval'],
    showIfPositiveInStage2: true,
    getValue: (app) => toNumber(app.amounts?.pending_payment),
  },
  {
    key: 'paid-amount',
    labelKey: 'common.tas.financial_details_summary.fields.paid_amount',
    section: 'reimbursement-details',
    stages: ['attend-pending-evidence', 'post-evidence-approval'],
    showIfPositiveInStage2: true,
    getValue: (app) => toNumber(app.amounts?.paid),
  },
  {
    key: 'course-cash-reserved',
    labelKey: 'common.tas.financial_details_summary.fields.course_cash_reserved',
    section: 'course-cash-details',
    stages: ['attend-pending-evidence', 'post-evidence-approval'],
    isCourseCash: true,
    getValue: (app) => Math.max(0, toNumber(app.amounts?.course_cash?.reserved) ?? 0),
  },
  {
    key: 'course-cash-used',
    labelKey: 'common.tas.financial_details_summary.fields.course_cash_used',
    section: 'course-cash-details',
    stages: ['attend-pending-evidence', 'post-evidence-approval'],
    isCourseCash: true,
    getValue: (app) => toNumber(app.amounts?.course_cash?.used),
  },
  {
    key: 'repayment-requested',
    labelKey: 'common.tas.financial_details_summary.fields.repayment_amount_requested',
    section: 'reimbursement-details',
    stages: ['attend-pending-evidence', 'post-evidence-approval'],
    isAsNeeded: true,
    showIfPositiveInStage2: true,
    getValue: (app) => toNumber(Math.abs(app.amounts?.refund_requested ?? 0)),
  },
  {
    key: 'repayment-paid-back',
    labelKey: 'common.tas.financial_details_summary.fields.repayment_amount_paid_back',
    section: 'reimbursement-details',
    stages: ['attend-pending-evidence', 'post-evidence-approval'],
    isAsNeeded: true,
    showIfPositiveInStage2: true,
    getValue: (app) => toNumber(Math.abs(app.amounts?.refund_paid_back ?? 0)),
  },
  {
    key: 'forgiven-amount',
    labelKey: 'common.tas.financial_details_summary.fields.forgiven_amount',
    section: 'reimbursement-details',
    stages: ['attend-pending-evidence', 'post-evidence-approval'],
    isAsNeeded: true,
    showIfPositiveInStage2: true,
    getValue: (app) => toNumber(Math.abs(app.amounts?.refund_forgiven ?? 0)),
  },
];

export function getWorkflowStage(app: TasApplicationModel) {
  const preCourseStates = new Set([
    'TAS.ApplicationState.CREATED',
    'TAS.ApplicationState.DEFINE_COURSES',
    'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    'TAS.ApplicationState.COURSES_APPROVED',
    'TAS.ApplicationState.COURSES_NOT_APPROVED',
    'TAS.ApplicationState.COURSES_REJECTED',
  ]);

  const attendAndEvidenceStates = new Set([
    'TAS.ApplicationState.ATTEND',
    'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    'TAS.ApplicationState.EVIDENCE_APPROVED',
    'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
    'TAS.ApplicationState.EVIDENCE_REJECTED',
  ]);

  if (preCourseStates.has(app.state)) {
    return 'pre-course-approval';
  }

  if (attendAndEvidenceStates.has(app.state)) {
    return 'attend-pending-evidence';
  }

  return 'post-evidence-approval';
}

export function getVisibleFields(app: TasApplicationModel, limitsData?: LimitsStatusDataSignature) {
  const stage = getWorkflowStage(app);
  const isCourseCashProgram = app.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  const isAttendOrPostEvidenceStage =
    stage === 'attend-pending-evidence' || stage === 'post-evidence-approval';
  const useBalanceMultiplier =
    app.tasProgramInstance?.tasProgramTemplate?.useBalanceMultiplierForProgramLimits || false;

  return FINANCIAL_FIELDS.filter((field) => {
    if (!field.stages.includes(stage)) {
      return false;
    }

    if (field.isCourseCash && !isCourseCashProgram) {
      return false;
    }

    if (field.isBalanceMultiplier && !useBalanceMultiplier) {
      return false;
    }

    return true;
  })
    .map((field) => ({
      ...field,
      value: field.getValue(app, limitsData),
      isCredits:
        field.isCapacityField && limitsData?.limit_capacity === 'TAS.LimitCapacity.CREDITS',
    }))
    .filter((field) => {
      if (field.isAsNeeded && (field.value ?? 0) <= 0) {
        return false;
      }

      if (isAttendOrPostEvidenceStage && field.showIfPositiveInStage2 && (field.value ?? 0) <= 0) {
        return false;
      }

      return true;
    });
}

export function getGroupedVisibleFields(
  app: TasApplicationModel,
  limitsData?: LimitsStatusDataSignature
): FinancialSectionGroup[] {
  const visibleFields = getVisibleFields(app, limitsData);

  // Group fields by section
  const fieldsBySection = new Map<FinancialSection, FinancialFieldWithValue[]>();

  for (const field of visibleFields) {
    const existing = fieldsBySection.get(field.section) || [];
    existing.push(field);
    fieldsBySection.set(field.section, existing);
  }

  // Return sections in order, only if they have fields
  return SECTION_ORDER.filter((section) => fieldsBySection.has(section)).map((section) => ({
    section,
    labelKey: SECTION_LABELS[section],
    fields: fieldsBySection.get(section) || [],
  }));
}
